import type { Timestamp } from 'firebase/firestore';
import { UserRole } from '../@constants/user-roles';
import { AppLocation } from './location';
import { ShopCategory } from './shop-category';

/**
 * Base user profile interface containing common properties shared between shop owners and individuals
 * @interface BaseUserProfile
 * @property {string} uid - Unique identifier for the user
 * @property {string} email - User's email address
 * @property {string} [firstName] - User's first name (optional)
 * @property {string} [middleName] - User's middle name (optional)
 * @property {string} [nickname] - User's preferred nickname (optional)
 * @property {string} [lastName] - User's last name (optional)
 * @property {string} [phone] - User's phone number (optional)
 * @property {string} [bio] - User's biography or description (optional)
 * @property {string} [profileImage] - URL to user's profile picture (optional)
 * @property {AppLocation} [preferredLocation] - User's preferred location (optional)
 * @property {Timestamp} [createdAt] - Timestamp when the user account was created (optional)
 * @property {Date} lastSeen - Date when the user was last seen online
 * @property {UserRole} role - User's role in the system
 * @property {boolean} [deleted] - Flag indicating if the user account is deleted (optional)
 * @property {any} [deletedAt] - Timestamp when the user account was deleted (optional)
 * @property {string | null} [deletedBy] - User ID of the person who deleted the account (optional)
 */
export interface BaseUserProfile {
  uid: string;
  email: string;
  firstName?: string;
  middleName?: string;
  nickname?: string;
  lastName?: string;
  phone?: string;
  bio?: string;
  yearsOfExperience?: string;
  portfolioUrl?: string;
  /** Date the member started their barbering career; null until set by the member. */
  careerStartDate?: Timestamp | Date | null;
  language?: string;
  profileImage?: string;
  preferredLocation?: AppLocation;
  createdAt?: Timestamp | any;
  lastSeen: Date | any;
  role: UserRole;
  deleted?: boolean;
  deletedAt?: any; // Firestore Timestamp or serverTimestamp()
  deletedBy?: string | null; // User ID of the person who deleted the shop
  updatedBy?: string | null; // User ID of the person who last updated the account
  updated?: any; // Firestore Timestamp or serverTimestamp()
  accountCapabilities?: AccountCapabilities;
  /** Navigation preference only; never use this field for authorization. */
  primaryShopId?: string | null;
}

export interface AccountCapabilities {
  client: boolean;
  professional: boolean;
}

export type AccountCapability = keyof AccountCapabilities;

export const hasAccountCapability = (
  user: UserProfile | null | undefined,
  capability: AccountCapability,
): boolean => {
  if (user?.accountCapabilities?.[capability] === true) return true;

  // Legacy profiles remain readable during the data migration; new authorization
  // paths should write and consume accountCapabilities instead of role.
  return capability === 'professional'
    ? user?.role === 'individual'
    : capability === 'client' && user?.role === 'client';
};

/**
 * Individual user profile for customers who rent chairs
 * @interface ClientUser
 * @extends BaseUserProfile
 * @property {'individual'} role - User role identifier for individual customers
 * @property {string[]} [favorites] - Array of favorited shop IDs
 * @property {string | null} [selectedShopId] - Currently active shop ID
 * @property {Object} [ratings] - User's rating information
 * @property {number} ratings.average - Average rating received
 * @property {number} ratings.count - Total number of ratings received
 */
export interface ClientUser extends BaseUserProfile {
  role: 'client';
}

export interface StaffUser extends BaseUserProfile {
  role: 'staff';
}

export interface IndividualUserPreferences {
  visibility: 'public' | 'private';
  allowMessagesFrom: 'clients' | 'shops' | 'all' | 'none';
  searchable: boolean;
  showProfileImage: boolean;
  showLocation: boolean;
  showContactDetails: boolean;
  allowShopInvitations: boolean;
  allowBookingRequests: boolean;
  notifications: IndividualUserNotificationPreferences;
  receiveMarketingEmails: boolean;
}

export interface IndividualUserNotificationPreferences {
  email: boolean;
  push: boolean;
  sms: boolean;
  bookingRequests: boolean;
  shopInvitations: boolean;
  appointmentReminders: boolean;
}

export const DEFAULT_INDIVIDUAL_USER_PREFERENCES: Readonly<IndividualUserPreferences> = {
  visibility: 'public',
  allowMessagesFrom: 'all',
  searchable: true,
  showProfileImage: true,
  showLocation: false,
  showContactDetails: false,
  allowShopInvitations: true,
  allowBookingRequests: true,
  notifications: {
    email: true,
    push: true,
    sms: false,
    bookingRequests: true,
    shopInvitations: true,
    appointmentReminders: true,
  },
  receiveMarketingEmails: false,
};
/**
 * Shop owner preferences for managing shop settings and notifications
 * @interface ShopOwnerPreferences
 * @property {'overview' | 'appointments' | 'members'} defaultDashboard - Default dashboard view for the shop owner
 * @property {boolean} allowMemberInvitations - Flag indicating if the shop owner allows member invitations
 * @property {boolean} requireMemberApproval - Flag indicating if member approval is required for new members
 * @property {ShopOwnerNotificationPreferences} notifications - Notification preferences for the shop owner
 * @property {boolean} receiveMarketingEmails - Flag indicating if the shop owner wants to receive marketing emails
 */
export interface ShopOwnerPreferences {
  defaultDashboard: 'overview' | 'appointments' | 'members';
  allowMemberInvitations: boolean;
  requireMemberApproval: boolean;
  notifications: ShopOwnerNotificationPreferences;
  receiveMarketingEmails: boolean;
}

/**
 * Shop owner notification preferences for managing notifications related to shop activities
 * @interface ShopOwnerNotificationPreferences
 * @property {boolean} email - Flag indicating if email notifications are enabled
 * @property {boolean} push - Flag indicating if push notifications are enabled
 * @property {boolean} sms - Flag indicating if SMS notifications are enabled
 * @property {boolean} newBooking - Flag indicating if notifications for new bookings are enabled
 * @property {boolean} memberRequests - Flag indicating if notifications for member requests are enabled
 * @property {boolean} cancellations - Flag indicating if notifications for cancellations are enabled
 * @property {boolean} dailySummary - Flag indicating if daily summary notifications are enabled
 */
export interface ShopOwnerNotificationPreferences {
  email: boolean;
  push: boolean;
  sms: boolean;
  newBooking: boolean;
  memberRequests: boolean;
  cancellations: boolean;
  dailySummary: boolean;
}

export const DEFAULT_SHOP_OWNER_PREFERENCES: Readonly<ShopOwnerPreferences> = {
  defaultDashboard: 'overview',
  allowMemberInvitations: true,
  requireMemberApproval: true,
  notifications: {
    email: true,
    push: true,
    sms: false,
    newBooking: true,
    memberRequests: true,
    cancellations: true,
    dailySummary: true,
  },
  receiveMarketingEmails: false,
};

/**
 * Individual user profile for customers who rent chairs
 * @interface IndividualUser
 * @extends BaseUserProfile
 * @property {'individual'} role - User role identifier for individual customers
 * @property {string[]} [favorites] - Array of favorited shop IDs
 * @property {string | null} [selectedShopId] - Currently active shop ID
 * @property {Object} [ratings] - User's rating information
 * @property {number} ratings.average - Average rating received
 * @property {number} ratings.count - Total number of ratings received
 */
export interface IndividualUser extends BaseUserProfile {
  role: 'individual';
  favorites?: string[]; // Array of favorited shopIds
  selectedShopId?: string | null; // Currently active shopId
  location: AppLocation;
  ratings?: {
    average: number;
    count: number;
  };
  preferences?: IndividualUserPreferences;
}

/**
 * Shop owner user profile for barbershop business owners
 * @interface ShopOwner
 * @extends BaseUserProfile
 * @property {'shop'} role - User role identifier for shop owners
 * @property {string} [stripeAccountId] - Stripe account ID for payment processing
 * @property {string[]} [shops] - Array of owned shop IDs
 * @property {string} [selectedShopId] - Currently active shop ID
 * @property {Object} [ratings] - Shop owner's rating information
 * @property {number} ratings.average - Average rating received
 * @property {number} ratings.count - Total number of ratings received
 * @property {Object} [preferences] - Shop owner's preferences
 */
export interface ShopOwner extends BaseUserProfile {
  role: 'shop';
  stripeAccountId?: string;
  shops?: string[]; // Array of owned shopIds
  selectedShopId?: string; // Currently active shopId
  businessName?: string;
  businessCategory?: ShopCategory;
  isDiscoverable?: boolean;
  searchKeywords?: string[];
  ratings?: {
    average: number;
    count: number;
  };
  preferences?: ShopOwnerPreferences;
}

/** Administrator user profile with elevated platform permissions. */
export interface AdminUser extends BaseUserProfile {
  role: 'admin';
  permissions?: string[];
}

/**
 * Union type representing all possible user profile types
 * @typedef {ShopOwner | IndividualUser | AdminUser} UserProfile
 */
export type UserProfile = ShopOwner | IndividualUser | ClientUser | StaffUser | AdminUser;

/**
 * Type guard function to check if a user is a shop owner
 * @function isShopOwner
 * @param {UserProfile | null | undefined} user - User profile to check
 * @returns {user is ShopOwner} True if user is a shop owner, false otherwise
 */
export const isShopOwner = (user: UserProfile | null | undefined): user is ShopOwner =>
  user?.role === 'shop';

/**
 * Type guard function to check if a user is an individual customer
 * @function isIndividualUser
 * @param {UserProfile | null | undefined} user - User profile to check
 * @returns {user is IndividualUser} True if user is an individual customer, false otherwise
 */
export const isIndividualUser = (user: UserProfile | null | undefined): user is IndividualUser =>
  user?.role === 'individual';

/** Type guard for platform administrators. */
export const isAdminUser = (user: UserProfile | null | undefined): user is AdminUser =>
  user?.role === 'admin';
